/* eslint-disable @typescript-eslint/no-explicit-any */
import { RefObject, useEffect, useRef } from 'react';
import { ScheduleComponent } from '@syncfusion/ej2-react-schedule';
import dayjs from 'dayjs';
import { HolidayInterface } from 'packages/blu-booking/src/interfaces/Holiday';

/**
 * Aggiorna il calendario Syncfusion quando TUTTI i dati sono pronti.
 *
 * Rileva la transizione isLoading: true → false e a quel punto:
 * 1. Assegna il nuovo dataSource e chiama dataBind() per sincronizzare gli eventi
 * 2. Chiama refresh() per ricreare le celle (necessario per renderCell
 *    che applica la colorazione degli orari lavorativi)
 *
 * Usa un ref per il dataSource così il setTimeout legge sempre il valore
 * più recente, evitando race condition con setState asincrono.
 */
export function useSchedulerRefresh(
  scheduleObj: RefObject<ScheduleComponent | null>,
  appointments: any[],
  holidays: HolidayInterface[],
  isLoading: boolean
) {
  const dataSourceRef = useRef([
    ...appointments,
    ...(holidays ?? []).map((holiday: HolidayInterface) => {
      const date = dayjs()
        .set('month', holiday.month - 1)
        .set('date', holiday.day);
      return {
        ...holiday,
        Id: holiday.id,
        StartTime: date.format('YYYY-MM-DDT00:00:00'),
        IsAllDay: true,
        Subject: holiday.name,
        EndTime: date.format('YYYY-MM-DDT23:59:59'),
        color: 'var(--base-color)',
      };
    }),
  ]);
  dataSourceRef.current = [
    ...appointments,
    ...(holidays ?? []).map((holiday: HolidayInterface) => {
      const date = dayjs()
        .set('month', holiday.month - 1)
        .set('date', holiday.day);
      return {
        ...holiday,
        Id: holiday.id,
        StartTime: date.format('YYYY-MM-DDT00:00:00'),
        IsAllDay: true,
        Subject: holiday.name,
        EndTime: date.format('YYYY-MM-DDT23:59:59'),
        color: 'var(--base-color)',
      };
    }),
  ];

  const prevIsLoading = useRef(isLoading);

  useEffect(() => {
    const wasLoading = prevIsLoading.current;
    prevIsLoading.current = isLoading;

    // Refresh solo alla transizione loading → ready
    if (!wasLoading || isLoading) return;
    if (!scheduleObj.current) return;

    try {
      if (!scheduleObj.current) return;
      const scheduler = scheduleObj.current;
      // scheduler.refreshTemplates('cellTemplate');

      // Aggiorna il dataSource e sincronizza con dataBind()
      scheduler.eventSettings.dataSource = dataSourceRef.current;
      scheduler.dataBind();
    } catch (error) {
      /* empty */
    }
  }, [scheduleObj, isLoading]);
}
